/**
 * Reconciliation view — the TRUTHFUL set of active Cloudflare rules (edge ↔ mirror),
 * not the recent-action feed. Distinguishes economic challenge / manual / trust /
 * manual exception, surfaces orphans (cf_only) and mirror ghosts, and lets a human
 * reactivate a manually-unblocked entity. Read + one reversible control (reactivate).
 */
import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { api, serverError } from '../../lib/api';
import { Badge, Btn, Card, CardHead } from './kit';

export function CfRulesView({ projectId }: { projectId: string }): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [thrInput, setThrInput] = useState('');
  const rulesQ = useQuery({
    queryKey: ['cf-rules', projectId],
    queryFn: () => api.cfRules(projectId),
    enabled: !!projectId,
  });
  const d = rulesQ.data;
  // Controlled field synced to the SAVED value — never shows a stale default.
  useEffect(() => {
    if (d) setThrInput(String(d.threshold));
  }, [d]);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['cf-rules', projectId] });
  const saveThreshold = useMutation({
    mutationFn: (n: number) => api.cfEconomicConfig(projectId, { threshold: n }),
    onSuccess: () => {
      toast.success(t('cfp.rulesThresholdSaved'));
      invalidate();
    },
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });
  const reactivate = useMutation({
    mutationFn: (ip: string) => api.cfReactivate(projectId, ip),
    onSuccess: () => {
      toast.success(t('cfp.reactivated'));
      invalidate();
    },
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });
  const orphan = useMutation({
    mutationFn: (v: { cfItemId: string; action: 'attach' | 'remove' }) =>
      api.cfOrphan(projectId, v.cfItemId, v.action),
    onSuccess: (r) => {
      toast.success(r.action === 'attach' ? t('cfp.rulesAttached') : t('cfp.rulesRemoved'));
      invalidate();
    },
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });
  return (
    <Card className="mb-4">
      <CardHead title={t('cfp.rulesTitle')} sub={t('cfp.rulesSub')} />

      {/* Economic policy context — the mandated wording. */}
      <div className="mb-3 flex items-start gap-2 rounded-lg border border-border bg-bg px-3.5 py-2 text-[12px] leading-snug text-text-3">
        <span>ℹ</span>
        <span>{t('cfp.rulesPolicy', { n: d?.threshold ?? 2, months: d?.periodMonths ?? 13 })}</span>
      </div>

      {/* Client setting — number of ad clicks allowed. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="text-[12px] text-text-2" htmlFor="cf-threshold">
          {t('cfp.rulesThresholdLabel')}
        </label>
        <input
          id="cf-threshold"
          type="number"
          min={1}
          max={100}
          value={thrInput}
          onChange={(e) => setThrInput(e.target.value)}
          className="w-20 rounded-lg border border-border-strong bg-surface px-2 py-1 text-[13px] outline-none focus:border-primary"
        />
        <Btn
          disabled={saveThreshold.isPending}
          onClick={() => {
            const n = Number(thrInput);
            if (Number.isInteger(n) && n >= 1 && n <= 100) saveThreshold.mutate(n);
          }}
        >
          {t('cfp.rulesThresholdSave')}
        </Btn>
      </div>

      {rulesQ.isLoading ? (
        <div className="h-24 animate-pulse rounded bg-neutral-bg" />
      ) : !d ? (
        <p className="py-4 text-[12.5px] text-text-3">{t('cf.errorGeneric')}</p>
      ) : (
        <>
          {/* Edge total is asserted ONLY after a live reconciliation. If Cloudflare is
              unreachable we say so — we never pass the mirror count off as the edge total. */}
          {d.edgeAvailable ? (
            <div className="mb-3 rounded-lg border border-clean-border bg-clean-bg px-3.5 py-2 text-[12.5px] font-medium text-clean-fg">
              {t('cfp.rulesEdgeSummary', {
                synced: t('units.ruleSynced', { count: d.summary.synced ?? 0 }),
                total: t('units.rulePresent', { count: d.summary.presentAtCf ?? 0 }),
              })}
            </div>
          ) : (
            <div className="mb-3 rounded-lg border border-[#f3dca6] bg-[#fdf6e8] px-3.5 py-2 text-[12.5px] leading-relaxed text-[#7c5212]">
              {d.edgeError === 'auth_expired'
                ? t('cfp.rulesEdgeAuthExpired')
                : d.summary.trackedBySilospot > 0
                  ? `${t('cfp.rulesEdgeUnavailable')} ${t('units.protectionTracked', { count: d.summary.trackedBySilospot })}`
                  : t('cfp.rulesEdgeUnavailable')}
            </div>
          )}

          {d.edgeAvailable && d.counts ? (
            <div className="mb-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              <Tile label={t('cfp.rulesBoth')} n={d.counts.both} tone="clean" />
              <Tile label={t('cfp.rulesCfOnly')} n={d.counts.cfOnly} tone="fraud" />
              <Tile label={t('cfp.rulesMirrorOnly')} n={d.counts.mirrorOnly} tone="fraud" />
              <Tile label={t('cfp.rulesTotal')} n={d.counts.total} tone="neutral" />
            </div>
          ) : null}

          {d.edgeAvailable ? (
            <>
              {d.rules.length === 0 ? (
                <p className="py-4 text-center text-[12.5px] text-text-3">{t('cfp.rulesEmpty')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead>
                      <tr>
                        {[
                          t('cfp.rulesColTarget'),
                          t('cfp.rulesColSource'),
                          t('cfp.rulesColKind'),
                          t('cfp.rulesColState'),
                          '',
                        ].map((h, i) => (
                          <th
                            key={h || `c${i}`}
                            className="whitespace-nowrap border-b border-border px-3 py-2 text-left text-[11px] font-semibold uppercase text-text-3"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.rules.map((r) => (
                        <tr
                          key={`${r.source}-${r.cfItemId ?? r.target}`}
                          className="hover:bg-[#fafbfc]"
                        >
                          <td className="border-b border-[#f1f3f6] px-3 py-2">
                            <span className="font-mono text-[12px] text-text">{r.target}</span>
                            <span className="ml-1.5 text-[11px] text-text-3">
                              {r.isV6 ? t('cfp.rulesV6') : t('cfp.rulesV4')}
                            </span>
                          </td>
                          <td className="border-b border-[#f1f3f6] px-3 py-2">
                            {r.source === 'both' ? (
                              <Badge tone="clean">{t('cfp.rulesSrcBoth')}</Badge>
                            ) : r.source === 'cf_only' ? (
                              <Badge tone="fraud">{t('cfp.rulesSrcCfOnly')}</Badge>
                            ) : (
                              <Badge tone="fraud">{t('cfp.rulesSrcMirrorOnly')}</Badge>
                            )}
                          </td>
                          <td className="border-b border-[#f1f3f6] px-3 py-2 text-[12px] text-text-2">
                            {r.origin === 'economic'
                              ? t('cfp.rulesKindEconomic')
                              : r.origin === 'auto'
                                ? t('cfp.rulesKindScore')
                                : r.origin === 'imported'
                                  ? t('cfp.rulesKindImported')
                                  : t('cfp.rulesKindManual')}
                            <div className="mt-0.5 text-[11px] text-text-3">
                              {r.scoreKnown
                                ? `${t('cfp.rulesScore')} ${r.score}`
                                : t('cfp.rulesScoreUnknown')}
                            </div>
                          </td>
                          <td className="border-b border-[#f1f3f6] px-3 py-2">
                            <div className="flex flex-wrap gap-1.5">
                              {r.trusted ? (
                                <Badge tone="clean">{t('cfp.rulesTrusted')}</Badge>
                              ) : null}
                              {r.excepted ? (
                                <Badge tone="info">{t('cfp.rulesExcepted')}</Badge>
                              ) : null}
                              {!r.trusted && !r.excepted ? (
                                <span className="text-[11.5px] text-text-3">—</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="border-b border-[#f1f3f6] px-3 py-2 text-right">
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {r.excepted && r.entity ? (
                                <button
                                  type="button"
                                  disabled={reactivate.isPending}
                                  onClick={() => r.entity && reactivate.mutate(r.entity)}
                                  className="rounded-md border border-border px-2.5 py-1 text-[12px] font-semibold text-accent-strong hover:bg-bg disabled:opacity-50"
                                >
                                  {t('cfp.rulesReactivate')}
                                </button>
                              ) : null}
                              {r.source === 'cf_only' && r.cfItemId ? (
                                <>
                                  {r.edgeSource === 'access_rule' ? (
                                    <button
                                      type="button"
                                      disabled={orphan.isPending}
                                      onClick={() =>
                                        r.cfItemId &&
                                        orphan.mutate({ cfItemId: r.cfItemId, action: 'attach' })
                                      }
                                      className="rounded-md border border-border px-2.5 py-1 text-[12px] font-semibold text-accent-strong hover:bg-bg disabled:opacity-50"
                                    >
                                      {t('cfp.rulesAttach')}
                                    </button>
                                  ) : (
                                    <span className="text-[11px] text-text-3">
                                      {t('cfp.rulesAttachUnavailable')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    disabled={orphan.isPending}
                                    onClick={() => {
                                      if (
                                        r.cfItemId &&
                                        window.confirm(t('cfp.rulesRemoveConfirm'))
                                      ) {
                                        orphan.mutate({ cfItemId: r.cfItemId, action: 'remove' });
                                      }
                                    }}
                                    className="rounded-md border border-[#f3c9c9] px-2.5 py-1 text-[12px] font-semibold text-fraud-fg hover:bg-fraud-bg disabled:opacity-50"
                                  >
                                    {t('cfp.rulesRemoveEdge')}
                                  </button>
                                </>
                              ) : null}
                              {orphan.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-text-3" />
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {d.counts && (d.counts.cfOnly > 0 || d.counts.mirrorOnly > 0) ? (
                <p className="mt-3 rounded-lg border border-[#f3dca6] bg-[#fdf6e8] px-3.5 py-2 text-[12px] leading-relaxed text-[#7c5212]">
                  {t('cfp.rulesDriftNote')}
                </p>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Card>
  );
}

function Tile({
  label,
  n,
  tone,
}: {
  label: string;
  n: number;
  tone: 'clean' | 'fraud' | 'neutral';
}): ReactElement {
  const cls =
    tone === 'fraud'
      ? 'border-[#f3c9c9] bg-fraud-bg text-fraud-fg'
      : tone === 'clean'
        ? 'border-clean-border bg-clean-bg text-clean-fg'
        : 'border-border bg-bg/40 text-text-2';
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${cls}`}>
      <div className="text-[17px] font-bold leading-none">{n}</div>
      <div className="mt-1 text-[11px] leading-tight">{label}</div>
    </div>
  );
}
